// client/src/pages/admin/ManageTemplates.js
//
// Admin page for managing templates.
//
// CHANGES IN THIS REVISION (Shareable Links & Reactions build):
//   - Added top-level tabs: Templates / Reactions
//   - Reactions tab renders <ReactionLibraryTab /> (File 20) for managing
//     the master emoji library.
//   - Templates tab is the existing page, with one new per-row button
//     "Reactions" that opens <TemplateReactionsModal /> (File 21) to
//     configure which emojis appear on cards using that template.
//   - All existing template management functionality preserved.

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import adminService from '../../services/adminService.js';
import Button from '../../components/ui/Button.js';
import Input, { Textarea, Select } from '../../components/ui/Input.js';
import Modal from '../../components/ui/Modal.js';
import ReactionLibraryTab from './ReactionLibraryTab.js';
import TemplateReactionsModal from './TemplateReactionsModal.js';

export default function ManageTemplates() {
  const navigate = useNavigate();

  // ─── Top-level tab state ────────────────────────────────────────────
  // 'templates' = existing template list (default)
  // 'reactions' = new emoji library manager
  const [activeTab, setActiveTab] = useState('templates');

  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [allMusic, setAllMusic] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showMusicModal, setShowMusicModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [musicTemplate, setMusicTemplate] = useState(null);
  const [selectedMusic, setSelectedMusic] = useState([]);
  const [defaultMusicId, setDefaultMusicId] = useState(null);
  const [form, setForm] = useState({
    categoryid: '', name: '', description: '', htmlcontent: '', preview_html: '',
    allowphoto: false, isbirthday: false, isavailable: false, previewimageurl: '',
    isbusiness: false, isfree: false, isadmin: false, industry_type_ids: [],
  });

  // ─── Reactions assignment modal state ───────────────────────────────
  // Set to a template object when admin clicks the Reactions button.
  // Null when closed. The modal owns its own load/save logic.
  const [reactionsTemplate, setReactionsTemplate] = useState(null);

  const load = () => {
    adminService.getTemplates().then((d) => setTemplates(d.templates));
    adminService.getMoonbeamCategories().then((d) => setCategories(d.categories));
    adminService.getIndustryTypes().then((d) => setIndustryTypes(d.industryTypes || []));
    adminService.getMusic().then((d) => setAllMusic(d.music || []));
  };
  useEffect(() => { load(); }, []);

  // Check if selected category is business (for hint only)
  const selectedCategory = categories.find(c => String(c.categoryid) === String(form.categoryid));
  const categoryIsBusiness = selectedCategory?.isbusiness || false;
  const showIndustryTypes = form.isbusiness;

  const editingTemplate = editing ? templates.find(t => t.templateid === editing) : null;
  const editingHasParts = editingTemplate?.has_parts || false;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...form,
      categoryid: parseInt(form.categoryid),
      industry_type_ids: form.isbusiness ? form.industry_type_ids : [],
      emailhtmlcontent: '',
    };
    if (editing) await adminService.updateTemplate(editing, data);
    else await adminService.createTemplate(data);
    setShowForm(false); setEditing(null); load();
  };

  const openNew = () => {
    setEditing(null);
    setForm({
      categoryid: '', name: '', description: '', htmlcontent: '', preview_html: '',
      allowphoto: false, isbirthday: false, isavailable: false, previewimageurl: '',
      isbusiness: false, isfree: false, isadmin: false, industry_type_ids: [],
    });
    setShowForm(true);
  };

  const startEdit = (t) => {
    setForm({
      ...t,
      categoryid: String(t.categoryid),
      preview_html: t.preview_html || '',
      isbusiness: t.isbusiness || false,
      isfree: t.isfree || false,
      isadmin: t.isadmin || false,
      industry_type_ids: (t.industryTypes || []).map(it => it.industrytypeid),
    });
    setEditing(t.templateid);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await adminService.deleteTemplate(id); load();
  };

  const toggleIndustryType = (id) => {
    setForm(prev => {
      const current = prev.industry_type_ids || [];
      if (current.includes(id)) {
        return { ...prev, industry_type_ids: current.filter(i => i !== id) };
      } else {
        return { ...prev, industry_type_ids: [...current, id] };
      }
    });
  };

  const handleCategoryChange = (e) => {
    const catId = e.target.value;
    const cat = categories.find(c => String(c.categoryid) === catId);
    setForm(prev => ({
      ...prev,
      categoryid: catId,
      isbusiness: cat?.isbusiness || prev.isbusiness,
    }));
  };

  // ——— Music assignment ———————————————————————————————
  const openMusicAssignment = async (template) => {
    setMusicTemplate(template);
    try {
      const res = await fetch(`/api/templates/${template.templateid}`);
      const data = await res.json();
      setSelectedMusic((data.music || []).map((m) => m.musicid));
      const defaultTrack = (data.music || []).find((m) => m.isdefault);
      setDefaultMusicId(defaultTrack ? defaultTrack.musicid : null);
    } catch {
      setSelectedMusic([]);
      setDefaultMusicId(null);
    }
    setShowMusicModal(true);
  };

  const toggleMusicSelection = (musicid) => {
    setSelectedMusic((prev) => {
      if (prev.includes(musicid)) {
        if (musicid === defaultMusicId) setDefaultMusicId(null);
        return prev.filter((id) => id !== musicid);
      }
      if (prev.length >= 4) return prev;
      return [...prev, musicid];
    });
  };

  const saveMusicAssignment = async () => {
    if (!musicTemplate) return;
    const musicIds = selectedMusic.map((id, i) => ({ musicid: id, displayorder: i + 1 }));
    const effectiveDefault = selectedMusic.includes(defaultMusicId) ? defaultMusicId : null;
    await adminService.assignMusic(musicTemplate.templateid, musicIds, effectiveDefault);
    setShowMusicModal(false);
    setMusicTemplate(null);
    load();
  };

  // ——— Reactions assignment ——————————————————————————————
  // Opens the modal for a specific template. The modal handles its own
  // fetch/save against /admin/templates/:id/reactions.
  const openReactionsAssignment = (template) => {
    setReactionsTemplate(template);
  };

  const handleReactionsSaved = () => {
    // No-op for now — the template list doesn't display reactions config
    // per row, so we don't need to refresh templates after a save. If we
    // later add a "Reactions: 3" badge per row, call load() here.
    setReactionsTemplate(null);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-gold">Templates</h1>
        {activeTab === 'templates' && <Button onClick={openNew}>Add Template</Button>}
      </div>

      {/* ─── Top-level tabs ────────────────────────────────────────── */}
      <div className="flex gap-2 mb-6 border-b border-royal-700/40">
        <button
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-2.5 text-base font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'templates'
              ? 'border-gold text-gold'
              : 'border-transparent text-royal-200 hover:text-gold'
          }`}
        >
          📄 Templates
        </button>
        <button
          onClick={() => setActiveTab('reactions')}
          className={`px-4 py-2.5 text-base font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'reactions'
              ? 'border-gold text-gold'
              : 'border-transparent text-royal-200 hover:text-gold'
          }`}
        >
          😀 Reactions
        </button>
      </div>

      {/* ════════════════════════════════════════════════════════════
          TEMPLATES TAB
          ════════════════════════════════════════════════════════════ */}
      {activeTab === 'templates' && (
        <>
          {/* Info about email templates being managed separately */}
          <div className="mb-4 p-3 bg-royal-800/40 border border-royal-600 rounded-lg">
            <p className="text-royal-200 text-sm">
              📧 <span className="text-gold font-medium">Email notification templates</span> are now managed separately per plan type.
              Go to <Link to="/admin/notification-email-templates" className="text-gold underline hover:text-gold-light">Email Templates</Link> to manage them.
            </p>
          </div>

          <div className="space-y-3">
            {templates.map((t) => (
              <div key={t.templateid} className={`bg-royal-800/40 border rounded-lg p-4 flex justify-between items-center ${
                t.isbusiness || t.categoryisbusiness ? 'border-purple-500/50' : 'border-royal-600'
              }`}>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white font-semibold">{t.name}</p>
                    {(t.isbusiness || t.categoryisbusiness) && (
                      <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                        Business
                      </span>
                    )}
                    {t.isadmin && (
                      <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                        Admin Only
                      </span>
                    )}
                    {t.preview_html && (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                        Has Preview
                      </span>
                    )}
                    {t.has_parts && (
                      <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-300 text-xs rounded-full">
                        Multi-Part
                      </span>
                    )}
                  </div>
                  <p className="text-royal-200 text-sm">
                    {t.categoryname} &middot; {t.isavailable ? 'Published' : 'Draft'}
                    {t.allowphoto && ' · Photo'}{t.isbirthday && ' · Birthday'}{t.isfree && ' · Free'}
                  </p>
                  {t.industryTypes && t.industryTypes.length > 0 && (
                    <p className="text-purple-400 text-xs mt-1">
                      Industries: {t.industryTypes.map(it => it.industryname).join(', ')}
                    </p>
                  )}
                </div>
                <div className="flex gap-3 flex-wrap justify-end">
                  <button
                    className="text-blue-400 text-sm hover:text-blue-300 transition-colors"
                    onClick={() => navigate(`/admin/templates/${t.templateid}/fields`)}
                  >
                    Fields
                  </button>
                  <button
                    className={`text-sm transition-colors ${
                      t.has_parts
                        ? 'text-cyan-300 hover:text-cyan-200 font-semibold'
                        : 'text-cyan-400 hover:text-cyan-300'
                    }`}
                    onClick={() => navigate(`/admin/templates/${t.templateid}/parts`)}
                    title={t.has_parts ? 'Manage HTML parts' : 'Convert to multi-part'}
                  >
                    Parts
                  </button>
                  <button
                    className="text-purple-400 text-sm hover:text-purple-300 transition-colors"
                    onClick={() => openMusicAssignment(t)}
                  >
                    Music
                  </button>
                  <button
                    className="text-pink-400 text-sm hover:text-pink-300 transition-colors"
                    onClick={() => openReactionsAssignment(t)}
                    title="Configure which reaction emojis appear on this card"
                  >
                    Reactions
                  </button>
                  <button className="text-gold text-sm hover:text-gold-light transition-colors" onClick={() => startEdit(t)}>Edit</button>
                  <button className="text-red-400 text-sm hover:text-red-300 transition-colors" onClick={() => handleDelete(t.templateid)}>Delete</button>
                </div>
              </div>
            ))}
            {templates.length === 0 && <p className="text-royal-200 text-center py-8">No templates yet.</p>}
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════
          REACTIONS TAB
          ════════════════════════════════════════════════════════════ */}
      {activeTab === 'reactions' && (
        <ReactionLibraryTab />
      )}

      {/* Edit/Create Template Modal */}
      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Template' : 'New Template'} maxWidth="max-w-3xl">
        <form onSubmit={handleSubmit} className="max-h-[75vh] overflow-y-auto pr-2">
          <Select label="Category" value={form.categoryid} onChange={handleCategoryChange} required>
            <option value="">Select...</option>
            {categories.map((c) => (
              <option key={c.categoryid} value={c.categoryid}>
                {c.categoryname}{c.isbusiness ? ' (Business)' : ''}
              </option>
            ))}
          </Select>
          <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <Input label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

          {/* HTML Content Section */}
          <div className="my-4 p-4 bg-royal-900/50 border border-royal-700 rounded-lg">
            <h3 className="text-gold font-medium mb-3">📄 HTML Content</h3>

            {editingHasParts && (
              <div className="mb-3 p-3 bg-cyan-500/10 border border-cyan-500/40 rounded-lg">
                <p className="text-cyan-200 text-sm">
                  🧩 <span className="font-semibold">This template uses parts mode.</span> The fields below are read-only — the live HTML is assembled from{' '}
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); navigate(`/admin/templates/${editing}/parts`); }}
                    className="underline hover:text-cyan-100"
                  >
                    Template Parts
                  </button>
                  . Edit content there.
                </p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm text-royal-200 mb-1.5">
                Final HTML <span className="text-royal-200">(what recipients see)</span>
              </label>
              <textarea
                value={form.htmlcontent}
                onChange={(e) => setForm({ ...form, htmlcontent: e.target.value })}
                required={!editingHasParts}
                readOnly={editingHasParts}
                rows={6}
                placeholder="The rendered card HTML - no forms, data injected via {{template_data_json}}"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm text-white placeholder-royal-200 focus:outline-none transition-colors font-mono resize-y ${
                  editingHasParts
                    ? 'bg-royal-800/30 border-royal-700 cursor-not-allowed'
                    : 'bg-royal-800/50 border-royal-600 focus:border-gold/50'
                }`}
              />
              <p className="text-royal-200 text-xs mt-1">
                Use <code className="text-gold">{"{{template_data_json}}"}</code> to access field values
              </p>
            </div>

            <div className="mb-2">
              <label className="block text-sm text-royal-200 mb-1.5">
                Preview HTML <span className="text-royal-200">(what senders see while creating)</span>
              </label>
              <textarea
                value={form.preview_html}
                onChange={(e) => setForm({ ...form, preview_html: e.target.value })}
                readOnly={editingHasParts}
                rows={6}
                placeholder="Interactive HTML with form inputs using data-fieldkey attribute"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm text-white placeholder-royal-200 focus:outline-none transition-colors font-mono resize-y ${
                  editingHasParts
                    ? 'bg-royal-800/30 border-royal-700 cursor-not-allowed'
                    : 'bg-royal-800/50 border-royal-600 focus:border-gold/50'
                }`}
              />
              <p className="text-royal-200 text-xs mt-1">
                Use <code className="text-gold">data-fieldkey="recipient_name"</code> on inputs. Optional - falls back to Final HTML if empty.
              </p>
            </div>
          </div>

          <Input label="Preview Image URL" value={form.previewimageurl} onChange={(e) => setForm({ ...form, previewimageurl: e.target.value })} />

          <div className="flex flex-wrap gap-6 my-4">
            <label className="flex items-center gap-2 text-royal-200 text-sm">
              <input type="checkbox" checked={form.allowphoto} onChange={(e) => setForm({ ...form, allowphoto: e.target.checked })} /> Allow Photo
            </label>
            <label className="flex items-center gap-2 text-royal-200 text-sm">
              <input type="checkbox" checked={form.isbirthday} onChange={(e) => setForm({ ...form, isbirthday: e.target.checked })} /> Birthday
            </label>
            <label className="flex items-center gap-2 text-royal-200 text-sm">
              <input type="checkbox" checked={form.isavailable} onChange={(e) => setForm({ ...form, isavailable: e.target.checked })} /> Published
            </label>
            <label className="flex items-center gap-2 text-purple-300 text-sm">
              <input
                type="checkbox"
                checked={form.isbusiness}
                onChange={(e) => setForm({ ...form, isbusiness: e.target.checked })}
              />
              Business Only
              {categoryIsBusiness && !form.isbusiness && (
                <span className="text-yellow-400 text-xs">(category is business - consider checking this)</span>
              )}
            </label>
            <label className="flex items-center gap-2 text-green-300 text-sm">
              <input
                type="checkbox"
                checked={form.isfree}
                onChange={(e) => setForm({ ...form, isfree: e.target.checked })}
              />
              Free (sendable by free users)
            </label>
            <label className="flex items-center gap-2 text-orange-300 text-sm">
              <input
                type="checkbox"
                checked={form.isadmin}
                onChange={(e) => setForm({ ...form, isadmin: e.target.checked })}
              />
              Admin Only (hidden from regular users)
            </label>
          </div>

          {showIndustryTypes && (
            <div className="my-4 p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg">
              <label className="block text-sm text-purple-300 mb-2 font-medium">
                Industry Types
              </label>
              <p className="text-purple-400 text-xs mb-3">
                Select which industries this template is for. "All Business" makes it available to everyone.
              </p>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {industryTypes.filter(it => it.isactive).map((it) => {
                  const isSelected = (form.industry_type_ids || []).includes(it.industrytypeid);
                  return (
                    <label
                      key={it.industrytypeid}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        isSelected ? 'bg-purple-500/20 text-purple-200' : 'text-royal-200 hover:bg-royal-800/50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleIndustryType(it.industrytypeid)}
                        className="w-4 h-4 rounded border-purple-500 bg-royal-800 text-purple-500 focus:ring-purple-500"
                      />
                      <span className="text-sm">{it.industryname}</span>
                    </label>
                  );
                })}
              </div>
              {(form.industry_type_ids || []).length === 0 && (
                <p className="text-yellow-400 text-xs mt-2">
                  ⚠️ No industries selected. Consider selecting "All Business" to show to all business users.
                </p>
              )}
            </div>
          )}

          <Button type="submit">{editing ? 'Update' : 'Create'}</Button>
        </form>
      </Modal>

      {/* Music Assignment Modal */}
      <Modal isOpen={showMusicModal} onClose={() => setShowMusicModal(false)} title={`Assign Music — ${musicTemplate?.name || ''}`} maxWidth="max-w-2xl">
        <div className="space-y-3">
          <p className="text-royal-200 text-sm">Select up to 4 music options and choose which is pre-selected by default.</p>

          <div className="px-4 py-3 rounded-lg border border-royal-700 bg-royal-900/30 flex items-center gap-3">
            <div className="w-5 h-5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-royal-200 text-base font-medium">🔇 No Music</p>
              <p className="text-royal-200 text-sm">Card opens silently</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <label className="flex items-center gap-1.5 text-sm text-royal-200 cursor-pointer">
                <input
                  type="radio"
                  name="defaultMusic"
                  checked={defaultMusicId === null}
                  onChange={() => setDefaultMusicId(null)}
                  className="w-4 h-4 text-gold bg-royal-800 border-royal-600 focus:ring-gold/50"
                />
                Default
              </label>
            </div>
          </div>

          {allMusic.length === 0 ? (
            <p className="text-royal-200 text-center py-6">No music created yet. Add music in the Music section first.</p>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {allMusic.map((m) => {
                const isSelected = selectedMusic.includes(m.musicid);
                const isDisabled = !isSelected && selectedMusic.length >= 4;
                const isDefault = defaultMusicId === m.musicid;

                return (
                  <div
                    key={m.musicid}
                    className={`rounded-lg border transition-all ${
                      isSelected
                        ? isDefault ? 'border-gold bg-gold/15' : 'border-gold bg-gold/10'
                        : isDisabled
                        ? 'border-royal-700 bg-royal-900/20 opacity-40'
                        : 'border-royal-700 bg-royal-900/30 hover:border-royal-500'
                    }`}
                  >
                    <div className="flex items-center gap-3 px-4 py-3">
                      <button
                        onClick={() => !isDisabled && toggleMusicSelection(m.musicid)}
                        disabled={isDisabled}
                        className="flex-shrink-0"
                      >
                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                          isSelected ? 'bg-gold border-gold' : 'border-royal-600'
                        }`}>
                          {isSelected && (
                            <svg className="w-3.5 h-3.5 text-royal-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </button>

                      <div className="flex-1 min-w-0">
                        <p className={`text-base font-medium ${isSelected ? 'text-gold' : 'text-royal-200'}`}>{m.name}</p>
                        {m.composer && <p className="text-royal-200 text-sm">{m.composer}</p>}
                        <p className="text-royal-200 text-sm">{m.musictype} · {m.isactive ? 'Active' : 'Inactive'}</p>
                      </div>

                      {isSelected && (
                        <label className="flex items-center gap-1.5 text-sm text-royal-200 cursor-pointer flex-shrink-0">
                          <input
                            type="radio"
                            name="defaultMusic"
                            checked={isDefault}
                            onChange={() => setDefaultMusicId(m.musicid)}
                            className="w-4 h-4 text-gold bg-royal-800 border-royal-600 focus:ring-gold/50"
                          />
                          Default
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between pt-3 border-t border-royal-700/30">
            <div>
              <p className="text-royal-200 text-sm">{selectedMusic.length} / 4 selected</p>
              <p className="text-royal-200 text-sm">
                Default: <span className="text-gold">{defaultMusicId === null ? 'No Music' : (allMusic.find(m => m.musicid === defaultMusicId)?.name || 'No Music')}</span>
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowMusicModal(false)}>Cancel</Button>
              <Button onClick={saveMusicAssignment}>Save</Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* ─── Reactions Assignment Modal ─────────────────────────── */}
      {reactionsTemplate && (
        <TemplateReactionsModal
          template={reactionsTemplate}
          onClose={() => setReactionsTemplate(null)}
          onSaved={handleReactionsSaved}
        />
      )}
    </div>
  );
}